<script setup lang="ts">
  import { ref, computed } from 'vue'
  import type { DateRange } from 'radix-vue'
  import {
    Link2,
    Calendar,
    MoreVertical,
    Trash2,
    Check,
    X,
    ArrowRightCircle,
    CheckCircle,
    CircleX
  } from 'lucide-vue-next'
  import { Button } from '@/components/ui/button'
  import { Badge } from '@/components/ui/badge'
  import { RangeCalendar } from '@/components/ui/range-calendar'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import PaginatedTableView from '@/components/admin/PaginatedTable.vue'
  import { TableCell, TableHead, TableRow } from '@/components/ui/table'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import {
    AppointmentsService,
    AppointmentProposalsService,
    type CustomerCase as Case
  } from '@/types/generated'
  import type { PaginatedResponse, FetchDataOptions } from '@/components/admin/PaginatedTable.vue'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
  } from '@/components/ui/dropdown-menu'
  import { useToast } from '@/components/ui/toast'
  import { genericSuccessToast } from '@/lib/toasts'
  import CreateAppointmentModal from '@/components/app/cases/appointmentCreationModal.vue'
  const store = useCaseStore()
  const { toast } = useToast()
  const tableRef = ref()
  const showDeleteDialog = ref(false)
  const appointmentToDelete = ref(null)

  // Method to refresh the table
  const refreshTable = async () => {
    if (store.caseRecord?.uuid) {
      await store.loadCase(store.caseRecord.uuid)
      if (tableRef.value) {
        await tableRef.value.refreshData()
      }
    }
  }

  // Filter states
  const selectedCase = ref('')
  const selectedLocation = ref('')
  const selectedStatus = ref('')

  // Initialize dateRange with empty values
  const dateRange = ref<DateRange>({
    start: undefined,
    end: undefined
  })

  const showCreateModal = ref(false)

  // Computed case workers from appointments and proposals
  const caseWorkers = computed(() => {
    const appointments = store.caseRecord?.appointments || []
    const proposals = store.caseRecord?.appointment_proposals || []

    const workers = new Set()

    appointments.forEach((apt) => {
      if (apt.calendar?.uuid && apt.calendar?.name !== 'Unbekannt') {
        workers.add(
          JSON.stringify({
            value: apt.calendar.uuid,
            title: apt.calendar.name
          })
        )
      }
    })

    proposals.forEach((prop) => {
      if (prop.calendar?.uuid && prop.calendar?.name !== 'Unbekannt') {
        workers.add(
          JSON.stringify({
            value: prop.calendar.uuid,
            title: prop.calendar.name
          })
        )
      }
    })

    return Array.from(workers).map((w) => JSON.parse(w))
  })

  const caseOptions = computed(() => caseWorkers.value)

  const locationOptions = [
    { value: 'ONLINE', title: 'Online' },
    { value: 'IN_PERSON', title: 'Vor Ort' }
  ]

  const statusOptions = [
    { value: 'CONFIRMED', title: 'Bestätigt' },
    { value: 'PENDING', title: 'Ausstehend' },
    { value: 'CANCELLED', title: 'Abgelehnt' }
  ]

  const getEndTime = (startTime: string, durationMins: number = 60) => {
    const startDate = new Date(startTime)
    const endDate = new Date(startDate.getTime() + durationMins * 60 * 1000)
    return endDate.toISOString()
  }

  const fetchAppointments = async (options?: FetchDataOptions): Promise<PaginatedResponse<any>> => {
    try {
      // Ensure we have the latest data
      if (store.caseRecord?.uuid) {
        await store.loadCase(store.caseRecord.uuid)
      }

      const appointments = store.caseRecord?.appointments || []
      const proposals = (store.caseRecord?.appointment_proposals || []).filter(
        (prop) => !prop.booked
      )

      // Merge and normalize appointments and proposals
      const allAppointments = [
        ...appointments.map((apt) => ({
          ...apt,
          isProposal: false
        })),
        ...proposals.map((prop) => ({
          ...prop,
          end: getEndTime(prop.start as string, prop.service?.duration_mins),
          status: 'LINK_SENT',
          isProposal: true
        }))
      ]

      const filtered = allAppointments.filter((apt) => {
        const matchesCase = !selectedCase.value || apt.calendar?.uuid === selectedCase.value
        const matchesLocation =
          !selectedLocation.value || apt.location_types === selectedLocation.value
        const matchesStatus = !selectedStatus.value || apt.status === selectedStatus.value
        const matchesSearch =
          !options?.search ||
          (apt.service?.name || '').toLowerCase().includes(options.search.toLowerCase()) ||
          (apt.calendar?.name || '').toLowerCase().includes(options.search.toLowerCase())

        // Add date range filtering
        let matchesDateRange = true
        if (dateRange.value?.start && dateRange.value?.end) {
          const aptDate = new Date(apt.start)
          const startDate = new Date(dateRange.value.start.toString())
          const endDate = new Date(dateRange.value.end.toString())
          matchesDateRange = aptDate >= startDate && aptDate <= endDate
        }

        return matchesCase && matchesLocation && matchesStatus && matchesSearch && matchesDateRange
      })

      return {
        results: filtered,
        count: filtered.length,
        next: null,
        previous: null
      }
    } catch (error) {
      console.error('Error fetching appointments:', error)
      throw error
    }
  }

  const getStatusBadge = (appointment: any) => {
    switch (appointment.status) {
      case 'TENTATIVE':
        return {
          class: 'flex gap-2',
          showActions: true
        }
      case 'CONFIRMED':
        return {
          text: 'Bestätigt',
          class: 'text-green-600',
          icon: 'CheckCircle'
        }
      case 'CANCELLED':
        return {
          text: 'Abgelehnt',
          class: 'text-red-600',
          icon: 'X'
        }
      case 'LINK_SENT':
        return {
          text: 'Link gesendet',
          class: 'flex items-center gap-1.5 text-blue-600',
          icon: 'ArrowRightCircle'
        }
      default:
        return {
          text: appointment.status,
          class: ''
        }
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const formatTime = (startDate: string, endDate: string) => {
    const start = new Date(startDate)
    const end = new Date(endDate)
    return `${start.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr - ${end.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr`
  }

  const handleDelete = async () => {
    if (!appointmentToDelete.value) return
    if (appointmentToDelete.value.isProposal) {
      await AppointmentProposalsService.appointmentProposalsDestroy({
        uuid: appointmentToDelete.value.uuid
      })
    } else {
      await AppointmentsService.appointmentsDestroy({
        uuid: appointmentToDelete.value.uuid
      })
    }

    genericSuccessToast()
    showDeleteDialog.value = false
    appointmentToDelete.value = null

    // Refresh the table after deletion
    await refreshTable()
  }

  const confirmAppointment = async (appointment: any) => {
    await AppointmentsService.appointmentsConfirmCreate({
      uuid: appointment.uuid,
      requestBody: {
        ...appointment,
        status: 'CONFIRMED'
      }
    })
    if (tableRef.value) {
      tableRef.value.refreshData()
    }

    genericSuccessToast()
  }

  const rejectAppointment = async (appointment: any) => {
    try {
      await AppointmentsService.appointmentsCancelCreate({
        requestBody: {
          ...appointment,
          status: 'CANCELLED'
        },
        uuid: appointment.uuid
      })
      if (tableRef.value) {
        tableRef.value.refreshData()
      }

      genericSuccessToast()
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Termin konnte nicht abgelehnt werden'
      })
    }
  }

  const handleFilterChange = () => {
    if (tableRef.value) {
      tableRef.value.refreshData()
    }
  }

  const truncateText = (text: string, length = 40) => {
    if (!text) return ''
    return text.length > length ? `${text.slice(0, length)}...` : text
  }
</script>

<template>
  <div class="mt-5 min-h-screen w-full space-y-6">
    <div class="rounded-xl bg-white p-3 shadow-sm ring-1 ring-gray-900/5 sm:p-4 lg:p-5">
      <!-- Action Buttons -->
      <div
        class="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:space-y-0"
      >
        <div class="flex flex-col gap-2">
          <h1 class="text-xl font-semibold">Termine</h1>
          <p class="text-sm text-gray-500">Hier sehen Sie alle Termine für diesen Fall.</p>
        </div>
        <Button
          class="flex h-10 w-full items-center justify-center gap-2 border border-gray-100 bg-white text-gray-900
            shadow-sm hover:bg-gray-50 sm:w-auto"
          variant="default"
          @click="showCreateModal = true"
        >
          + Termin hinzufügen
        </Button>
      </div>

      <!-- Table with Filters -->
      <PaginatedTableView
        ref="tableRef"
        :fetch-data="fetchAppointments"
        :searchable-fields="'Suchen'"
        class="overflow-x-auto rounded-xl border-none bg-white pt-4"
      >
        <template #actions>
          <div class="flex flex-col space-y-2 sm:flex-row sm:items-center sm:gap-4 sm:space-y-0">
            <SelectField
              v-model="selectedCase"
              :options="caseOptions"
              name="case"
              placeholder="Sachbearbeiter"
              :allow-unselect="true"
              unselected-option-text="Sachbearbeiter"
              class="w-full sm:w-40"
              @update:model-value="handleFilterChange"
            />
            <SelectField
              v-model="selectedLocation"
              :options="locationOptions"
              name="location"
              placeholder="Standort"
              :allow-unselect="true"
              unselected-option-text="Standort"
              class="w-full sm:w-40"
              @update:model-value="handleFilterChange"
            />
            <SelectField
              v-model="selectedStatus"
              :options="statusOptions"
              name="status"
              placeholder="Status"
              :allow-unselect="true"
              unselected-option-text="Status"
              class="w-full sm:w-40"
              @update:model-value="handleFilterChange"
            />
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="secondary"
                  class="w-full gap-2 sm:w-auto"
                >
                  <Calendar class="h-4 w-4" />
                  {{
                    dateRange?.start
                      ? formatDate(dateRange.start.toString()) +
                        ' - ' +
                        formatDate(dateRange.end.toString())
                      : 'Zeitraum'
                  }}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                class="w-auto p-0"
                align="start"
              >
                <RangeCalendar
                  v-model="dateRange"
                  class="rounded-md border"
                  @update:model-value="handleFilterChange"
                />
              </PopoverContent>
            </Popover>
          </div>
        </template>

        <template #table-head>
          <TableHead class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">
            Termin
          </TableHead>
          <TableHead class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
            Sachbearbeiter
          </TableHead>
          <TableHead class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
            Datum
          </TableHead>
          <TableHead class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
            Zeit
          </TableHead>
          <TableHead class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
            Standort
          </TableHead>
          <TableHead class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
            Status
          </TableHead>
        </template>

        <template #table-row="{ items }">
          <TableRow
            v-for="appointment in items"
            :key="appointment.uuid"
            class="group hover:bg-gray-50"
          >
            <TableCell class="py-4 pl-4 pr-3">
              <div
                class="max-w-xs truncate"
                :title="appointment.summary || appointment.service?.name"
              >
                {{ truncateText(appointment.summary || appointment.service?.name || 'Unbekannt') }}
              </div>
            </TableCell>
            <TableCell class="px-3 py-4">
              <div class="flex items-center gap-2">
                <div
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-purple-500 text-white"
                >
                  {{ appointment.calendar?.name?.charAt(0) || 'U' }}
                </div>
                <div>
                  <div class="font-medium">{{ appointment.calendar?.name || 'Unbekannt' }}</div>
                  <div
                    v-if="!appointment.isProposal"
                    class="text-sm text-gray-500"
                  >
                    {{ appointment.calendar?.calendar_name }}
                  </div>
                </div>
              </div>
            </TableCell>
            <TableCell class="px-3 py-4">
              {{ formatDate(appointment.start) }}
            </TableCell>
            <TableCell class="min-w-[180px] px-3 py-4">
              {{ formatTime(appointment.start, appointment.end) }}
            </TableCell>
            <TableCell class="px-3 py-4">
              {{ appointment.location_types === 'ONLINE' ? 'Online' : 'Vor Ort' }}
            </TableCell>
            <TableCell class="px-3 py-4">
              <div class="flex items-center justify-between">
                <div class="flex-grow">
                  <div
                    v-if="getStatusBadge(appointment).showActions"
                    class="flex gap-2"
                  >
                    <Button
                      variant="default"
                      class="bg-green-600 hover:bg-green-700"
                      @click="confirmAppointment(appointment)"
                    >
                      Bestätigen
                    </Button>
                    <Button
                      variant="secondary"
                      class="text-red-600 hover:text-red-700"
                      @click="rejectAppointment(appointment)"
                    >
                      Ablehnen
                    </Button>
                  </div>
                  <div
                    v-else
                    class="inline-flex items-center whitespace-nowrap"
                    :class="getStatusBadge(appointment).class"
                  >
                    <ArrowRightCircle
                      v-if="getStatusBadge(appointment).icon === 'ArrowRightCircle'"
                      class="mr-0 h-5 w-5 flex-shrink-0 p-0"
                    />
                    <CheckCircle
                      v-else-if="getStatusBadge(appointment).icon === 'CheckCircle'"
                      class="mr-1.5 h-4 w-4 flex-shrink-0"
                    />
                    <CircleX
                      v-else-if="getStatusBadge(appointment).icon === 'X'"
                      class="mr-1.5 h-4 w-4 flex-shrink-0"
                    />
                    <span>{{ getStatusBadge(appointment).text }}</span>
                  </div>
                </div>

                <div
                  v-if="!getStatusBadge(appointment).showActions"
                  class="ml-4 flex-shrink-0"
                >
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                      >
                        <span class="sr-only">Aktionen öffnen</span>
                        <MoreVertical class="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        class="text-red-600 focus:bg-red-50 focus:text-red-600"
                        @click="
                          () => {
                            appointmentToDelete = appointment
                            showDeleteDialog = true
                          }
                        "
                      >
                        <Trash2 class="mr-2 h-4 w-4" />
                        Löschen
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </TableCell>
          </TableRow>
        </template>
      </PaginatedTableView>
    </div>
  </div>

  <!-- Create Appointment Modal -->
  <CreateAppointmentModal
    v-model:open="showCreateModal"
    :case="store.caseRecord"
    @appointment-created="refreshTable"
  />

  <!-- Delete Confirmation Dialog -->
  <Dialog
    :open="showDeleteDialog"
    @update:open="showDeleteDialog = false"
  >
    <DialogContent class="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Termin löschen</DialogTitle>
        <DialogDescription>
          Möchten Sie diesen Termin wirklich löschen? Diese Aktion kann nicht rückgängig gemacht
          werden.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter class="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
        <Button
          variant="secondary"
          class="mt-2 sm:mt-0"
          @click="() => (showDeleteDialog = false)"
        >
          Abbrechen
        </Button>
        <Button
          variant="destructive"
          @click="handleDelete"
        >
          Löschen
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
